import React, { useId } from 'react';
import 'primeicons/primeicons.css';
import { MembershipModel } from 'services/EpdClient';
import { RoleName } from 'constants/constants';
import Tooltip from 'components/controls/Tooltip';
import styled from 'styled-components';
import FontIcon from 'components/icons/FontIcon';

interface UserRolesTooltipProps {
  memberships: MembershipModel[];
}
const UserRolesTooltip: React.FC<UserRolesTooltipProps> = ({ memberships }) => {
  const id = useId();

  const tooltipContent = () => {
    const uniqueRoles = [...new Set(memberships.map((m) => m.roleName))];
    return (
      <RolesExplanationContainer>
        {uniqueRoles.map((role) => {
          switch (role.toLowerCase()) {
            case RoleName.EPD_OWNER.toLowerCase():
              return (
                <RoleExplanation key={role}>
                  <BoldRoleName>EPD Owner</BoldRoleName>
                  <CanCantDiv>
                    As an EPD Owner, you can:
                  </CanCantDiv>
                  <RoleList>
                    <li>Create new EPDs and update existing ones.</li>
                    <li>Access all EPDs under your organisation.</li>
                    <li>Invite LCA Practitioners and Verifiers to work on specific EPDs.</li>
                    <li>Send EPDs for verification and publication.</li>
                    <li>Depublish or deregister EPDs.</li>
                    <li>Update your company details in the settings.</li>
                  </RoleList>
                </RoleExplanation>
              );
            case RoleName.LCA_PRACTITIONER.toLowerCase():
              return (
                <RoleExplanation key={role}>
                  <BoldRoleName>LCA Practitioner</BoldRoleName>
                  <CanCantDiv>As an LCA Practitioner, you can:</CanCantDiv>
                  <RoleList>
                    <li>Be invited to work on specific EPDs and access them.</li>
                    <li>Support EPD Owners by filling in the required information for EPDs.</li>
                  </RoleList>
                  <CanCantDiv>You cannot:</CanCantDiv>
                  <RoleList>
                    <li>Create or register new EPDs.</li>
                    <li>Depublish or deregister EPDs.</li>
                  </RoleList>
                </RoleExplanation>
              );
            case RoleName.EPD_VERIFIER.toLowerCase():
              return (
                <RoleExplanation key={role}>
                  <BoldRoleName>EPD Verifier</BoldRoleName>
                  <CanCantDiv>As an EPD Verifier, you can:</CanCantDiv>
                  <RoleList>
                    <li>Be invited to verify specific EPDs and access them.</li>
                    <li>Upload verification reports for EPDs.</li>
                    <li>Approve or decline EPDs during verification.</li>
                  </RoleList>
                  <CanCantDiv>You cannot:</CanCantDiv>
                  <RoleList>
                    <li>Create, register, or update EPDs.</li>
                  </RoleList>
                </RoleExplanation>
              );
            default:
              return null;
          }
        })}
      </RolesExplanationContainer>
    );
  };

  return (
    <>
      <QuestionIconSpan id={id}>
        <FontIcon icon="help-circle" style={{ marginTop: '3px' }} />
      </QuestionIconSpan>
      <Tooltip targetId={id} position={'mouse'} offset={1}>
        {tooltipContent()}
      </Tooltip>
    </>
  );
};

export default UserRolesTooltip;

const RolesExplanationContainer = styled.div`
  padding: 6px 8px;
`;

const RoleExplanation = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0px;
  }
`;

const BoldRoleName = styled.span`
  font-weight: 700;
  font-style: bold;
  font-size: 12px;
  font-family: inherit;
  line-height: 100%;
`;

const RoleList = styled.ul`
  margin-top: 4px;
  padding-left: 18px;
  margin-bottom: 0;
`;

const CanCantDiv = styled.div`
  margin-top: 4px;
`;

const QuestionIconSpan = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.primaryGreen};
  line-height: 1;
`;
